import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/useT';
import './FeaturesGrid.css';

const FEATURE_META = [
  { color: '#EFF6FF', iconColor: '#2563EB', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 7.5V6a2 2 0 012-2h3M3 16.5V18a2 2 0 002 2h3M21 7.5V6a2 2 0 00-2-2h-3M21 16.5V18a2 2 0 01-2 2h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><rect x="7" y="7" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/></svg> },
  { color: '#F0F4FF', iconColor: '#6366F1', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M21 21l-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 11h6M11 8v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg> },
  { color: '#F0FDF4', iconColor: '#10B981', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="7" cy="6" r="2" fill="currentColor" opacity="0.3"/><circle cx="7" cy="12" r="2" fill="currentColor" opacity="0.3"/><circle cx="7" cy="18" r="2" fill="currentColor" opacity="0.3"/></svg> },
  { color: '#FFF7ED', iconColor: '#F59E0B', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M10 2L3 7v5c0 5 3.5 9.5 7 11 3.5-1.5 7-6 7-11V7L10 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M7.5 12l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> },
  { color: '#FDF4FF', iconColor: '#A855F7', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="5" y="2" width="10" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/><rect x="9" y="8" width="10" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/></svg> },
  { color: '#EFF6FF', iconColor: '#0EA5E9', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2v10l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/></svg> },
  { color: '#F0FDF4', iconColor: '#14B8A6', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="3" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.5"/><rect x="13" y="3" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.5"/><rect x="3" y="13" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.5"/><rect x="13" y="13" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.5"/></svg> },
  { color: '#FFF7ED', iconColor: '#EC4899', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg> },
];

export default function FeaturesGrid({ limit = 8, showCTA = true }) {
  const t = useT();
  const f = t.features;
  const keys = ['scan', 'ocr', 'org', 'search', 'export', 'sync', 'security', 'offline'];
  const features = (limit ? keys.slice(0, limit) : keys).map((key, i) => ({
    ...FEATURE_META[i],
    title: f.items[key].title,
    description: f.items[key].desc,
  }));

  return (
    <section className="features-grid section" aria-labelledby="features-heading">
      <div className="container">
        <div className="section-header">
          <div className="badge">{f.badge}</div>
          <h2 id="features-heading">
            {f.headlineAccent
              ? f.headline.split(f.headlineAccent).map((part, i, arr) =>
                  i < arr.length - 1
                    ? <React.Fragment key={i}>{part}<span className="gradient-text">{f.headlineAccent}</span></React.Fragment>
                    : part
                )
              : f.headline}
          </h2>
          <p>{f.sub}</p>
        </div>

        <div className="features-grid__items">
          {features.map((feature, i) => (
            <article
              key={i}
              className="features-grid__card card"
              style={{ '--feature-color': feature.color, '--feature-icon-color': feature.iconColor }}
            >
              <div className="features-grid__icon" aria-hidden="true">
                <span style={{ color: feature.iconColor }}>{feature.icon}</span>
              </div>
              <h3 className="features-grid__title">{feature.title}</h3>
              <p className="features-grid__desc">{feature.description}</p>
            </article>
          ))}
        </div>

        {showCTA && (
          <div className="features-grid__cta">
            <Link to="/features" className="btn btn-secondary">
              {f.viewAll}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
